/**
 * Maps a wire failure onto the seam's `ApiError`, in one place.
 *
 * Three body shapes reach a client, and only one of them is ours:
 *
 *   1. An RFC 9457 problem document from the service — `application/problem+json`
 *      with a stable `type` URI to switch on and a `detail` safe to show.
 *   2. API Gateway's bare `{"message":"Unauthorized"}` on a missing/invalid
 *      token, and `{"message":"Too Many Requests"}` on throttling. Neither is a
 *      problem document; both are recognized by status code.
 *   3. Nothing at all — the network failed and `fetch` threw.
 *
 * Screens never see any of this. They see `ApiError`, whose `retryable` flag is
 * what `AsyncBoundary` and the query retry policy branch on, and whose `fields`
 * map carries a validation problem's per-field errors for inline rendering.
 */

import { problemTypes, type Problem } from '@match-point/api-client-ts'

import { ApiError } from './contract'

/** The shape `ValidationProblem.errors[]` entries take on the wire. */
interface WireValidationError {
  pointer?: string
  code?: string
  detail?: string
}

function isProblem (body: unknown): body is Problem & { errors?: WireValidationError[] } {
  return (
    typeof body === 'object' &&
    body !== null &&
    typeof (body as Problem).type === 'string' &&
    typeof (body as Problem).status === 'number'
  )
}

const RETRYABLE_TYPES: readonly string[] = [problemTypes.timeout, problemTypes.unavailable, problemTypes.internal]

/**
 * `pointer` is an RFC 6901 JSON Pointer into the request body
 * (`/condition_ids/1`). Screens key their field errors by the top-level member,
 * so `/contact/phone` files under `contact` — precise enough to place the
 * message next to the right control without every screen parsing pointers.
 */
function fieldsFrom (errors: WireValidationError[] | undefined): Record<string, string> | undefined {
  if (!errors || errors.length === 0) return undefined
  const fields: Record<string, string> = {}
  for (const entry of errors) {
    const segment = entry.pointer?.split('/')[1]
    if (!segment) continue
    fields[segment] = entry.detail ?? entry.code ?? 'Not acceptable.'
  }
  return Object.keys(fields).length > 0 ? fields : undefined
}

export function toApiError (status: number, body: unknown): ApiError {
  if (isProblem(body)) {
    const retryable = RETRYABLE_TYPES.includes(body.type ?? '') || status === 408 || status === 429 || status >= 500
    const fields = fieldsFrom(body.errors)
    return new ApiError(body.detail ?? body.title ?? 'The request failed.', {
      status,
      retryable,
      ...(fields ? { fields } : {})
    })
  }

  // The gateway's two non-problem bodies, recognized by status. A 401 here
  // means the token itself was refused before the service ran — expired,
  // usually — which is a sign-in problem rather than a retry problem.
  if (status === 401) {
    return new ApiError('Your session has expired. Sign in again.', { status, retryable: false })
  }
  if (status === 429) {
    return new ApiError('Too many requests. Wait a moment and try again.', { status, retryable: true })
  }

  return new ApiError('The request failed.', { status, retryable: status >= 500 })
}

/** The network failed before any response existed. Always worth one retry. */
export function networkError (cause: unknown): ApiError {
  const error = new ApiError('The service could not be reached.', { status: 0, retryable: true })
  error.cause = cause
  return error
}
